class SearchModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.searchInput = this.querySelector('input[type="search"]');
    this.predictiveSearch = this.querySelector('predictive-search');

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.reset.bind(this));

    // Handle mobile close button
    const mobileCloseButton = this.querySelector('.mobile-close-search');
    if (mobileCloseButton) {
      mobileCloseButton.addEventListener('click', this.close.bind(this));
    }

    this.summaryToggle.setAttribute('role', 'button');

    // Add event listeners for popular search terms
    this.setupPopularSearchTerms();

    // Add this after other event listeners in the constructor:
    const searchWrapper =
      this.closest('.search-modal')?.querySelector('.search-recommendations-wrapper') ||
      document.querySelector('.search-recommendations-wrapper');
    if (this.searchInput) {
      const field = this.searchInput.closest('.field');
      this.searchInput.addEventListener('input', () => {
        this.updateSummaryWithQuery();
        if (this.searchInput.value) {
          field.classList.add('input-not-empty');
        } else {
          field.classList.remove('input-not-empty');
          searchWrapper.classList.remove('results-empty');
        }
      });
    }
  }

  updateSummaryWithQuery() {
    const summaryContent = this.summaryToggle.querySelector('.search-summary-content');
    const placeholder = summaryContent.querySelector('.search-summary-placeholder');
    const querySpan = summaryContent.querySelector('.search-summary-query');
    const value = this.searchInput.value.trim();
    const closeIcon = this.summaryToggle.querySelector('.icon-close');
    const searchWrapper = this.querySelector('.search-recommendations-wrapper');

    if (value) {
      querySpan.textContent = value;
      querySpan.style.display = '';
      placeholder.style.display = 'none';
      closeIcon.style.display = 'block';
      searchWrapper.classList.add('full-height');
    } else {
      querySpan.textContent = '';
      querySpan.style.display = 'none';
      placeholder.style.display = '';
      closeIcon.style.display = 'none';
      searchWrapper.classList.remove('full-height');
    }
  }
  setupPopularSearchTerms() {
    const popularSearchTerms = this.querySelectorAll('.popular-search-term');
    popularSearchTerms.forEach((term) => {
      term.addEventListener('click', this.onPopularSearchTermClick.bind(this));
    });
  }

  onPopularSearchTermClick(event) {
    event.preventDefault();
    const searchTerm = event.target.getAttribute('data-search-term');

    if (searchTerm && this.searchInput) {
      // Set the search input value
      this.searchInput.value = searchTerm;

      // Focus the input
      this.searchInput.focus();

      // Trigger input event to activate predictive search if enabled
      if (this.predictiveSearch) {
        const inputEvent = new Event('input', { bubbles: true });
        this.searchInput.dispatchEvent(inputEvent);
      }
    }
  }

  onSummaryClick(event) {
    event.preventDefault();

    // Check if the clicked element is the close icon
    const closeIcon = event.target.closest('.icon-close');
    if (closeIcon) {
      // If close icon is clicked, clear the search and then open the modal
      this.reset(event);
      // Force open the modal after clearing
      if (!event.target.closest('details').hasAttribute('open')) {
        this.open(event);
      }
      return;
    }

    // Normal summary click behavior
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close();
  }

  open() {
    setScrollbarWidth();
    setHeaderBottomPosition();

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);

    // Clean up any leftover closing class
    const searchModal = this.querySelector('.search-modal');
    if (searchModal) {
      searchModal.classList.remove('closing');
    }

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('search-modal--open');

    // Add or remove 'full-height' class based on input value when modal opens
    const searchWrapper = this.querySelector('.search-recommendations-wrapper');
    if (this.searchInput && searchWrapper) {
      if (this.searchInput.value.trim()) {
        searchWrapper.classList.add('full-height');
      } else {
        searchWrapper.classList.remove('full-height');
      }
    }

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close() {
    removeTrapFocus();

    // Check if we're on mobile and add closing animation
    if (window.innerWidth <= 749) {
      const searchModal = this.querySelector('.search-modal');
      if (searchModal) {
        searchModal.classList.add('closing');

        // Wait for animation to complete before actually closing
        setTimeout(() => {
          this.completeClose();
        }, 250); // Match CSS animation duration (--duration-default)
      } else {
        this.completeClose();
      }
    } else {
      this.completeClose();
    }
  }

  completeClose() {
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('search-modal--open');

    // Remove closing class after modal is closed
    const searchModal = this.querySelector('.search-modal');
    if (searchModal) {
      searchModal.classList.remove('closing');
    }

    document.dispatchEvent(new CustomEvent('searchmodal:close'));
    this.updateSummaryWithQuery();
  }

  reset(event) {
    event.preventDefault();
    if (this.searchInput) {
      this.searchInput.value = '';
      this.updateSummaryWithQuery();
      this.searchInput.focus();

      // Trigger input event to reset predictive search state
      if (this.predictiveSearch) {
        const inputEvent = new Event('input', { bubbles: true });
        this.searchInput.dispatchEvent(inputEvent);
      }
    }
  }
}

customElements.define('search-modal', SearchModal);
// Debounce utility
function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

if (!customElements.get('search-from')) {
  class SearchFrom extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.searchIcon = null;
      this.searchClose = null;
      this.debouncedHandleInput = debounce(this.handleInput.bind(this), 300);

      this.positionBestseller();
    }

    connectedCallback() {
      this.input = this.querySelector('.search__field');
      this.searchIcon = this.querySelector('.search__icon');
      this.searchClose = this.querySelector('.search__close');
      if (this.input) {
        this.input.addEventListener('input', this.debouncedHandleInput);
      }
      this.searchIcon.addEventListener('click', this.formSubmit.bind(this));
      this.searchClose.addEventListener('click', this.formReset.bind(this));
    }

    formSubmit() {
      document.querySelector('.search__form').submit();
    }

    formReset() {
      if (this.input) {
        this.input.value = '';
      }
      const languagePrefix = this.getLanguagePrefix();
      const newUrl = `${languagePrefix}/search?q=&type=product`;
      window.history.replaceState({}, '', newUrl);
      this.renderSearchResults('');
      this.searchClose.classList.add('hidden');
    }

    handleInput(event) {
      const value = event.target.value;
      const languagePrefix = this.getLanguagePrefix();
      const newUrl = `${languagePrefix}/search?q=${encodeURIComponent(value)}&type=product`;
      window.history.replaceState({}, '', newUrl);
      this.renderSearchResults(value);
      if (value == '') {
        this.searchClose.classList.add('hidden');
      } else {
        this.searchClose.classList.remove('hidden');
      }
    }

    renderSearchResults(query) {
      const languagePrefix = this.getLanguagePrefix();
      fetch(`${languagePrefix}/search?q=${encodeURIComponent(query)}&type=product`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then((response) => response.text())
        .then((data) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');
          const resultsContainer = document.querySelector('.template-search__results');

          if (resultsContainer) {
            const newResults = doc.querySelector('.template-search__results');
            if (newResults) {
              resultsContainer.innerHTML = newResults.innerHTML;
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching main-search.liquid:', error);
        });
    }

    getLanguagePrefix() {
      const pathname = window.location.pathname;
      // Check if the URL starts with a language code (e.g., /en/, /fr/, /de/, etc.)
      const languageMatch = pathname.match(/^\/([a-z]{2})\//);
      return languageMatch ? `/${languageMatch[1]}` : '';
    }

    positionBestseller() {
      const bestseller = document.querySelector('body.template-search .product-slider__wrapper');

      console.log(bestseller);

      setTimeout(() => {
        bestseller && (bestseller.style.position = 'absolute');
      }, 2000);
    }
  }

  customElements.define('search-from', SearchFrom);
}
